import { db } from '../firebase';
import {
    collection, doc, getDoc, getDocs, setDoc, deleteDoc, writeBatch, getDocFromServer
} from 'firebase/firestore';

const COLLECTION_LOCATIONS = 'locations';

export const locationService = {
    async saveLocation(locationName, locationData) {
        // We intercept the save to write hierarchy to subcollections if it changes
        const docRef = doc(db, COLLECTION_LOCATIONS, locationName);
        const { hierarchy, ...flattenedData } = locationData;

        try {
            // 1. Save main document (without hierarchy blob)
            // If 'hierarchyUrl' exists, we might want to keep it or delete it? 
            // For now, let's allow it to be overwritten or nulled if passed.
            // But we specifically do NOT save 'hierarchy' blob to main doc.

            // If hierarchy is passed, we write it to subcollections
            if (hierarchy) {
                const batch = writeBatch(db);

                // Write parent, merging other fields
                batch.set(docRef, flattenedData, { merge: true });

                // 1. Get existing halls to detect deletions
                const hallsCol = collection(db, COLLECTION_LOCATIONS, locationName, 'halls');
                const existingHallsSnap = await getDocs(hallsCol);
                const existingHallIds = new Set();
                existingHallsSnap.forEach(doc => existingHallIds.add(doc.id));

                const newHallIds = new Set(Object.keys(hierarchy));

                // 2. Delete halls that are in DB but NOT in new hierarchy
                for (const existingId of existingHallIds) {
                    if (!newHallIds.has(existingId)) {
                        const hallRef = doc(db, COLLECTION_LOCATIONS, locationName, 'halls', existingId);
                        batch.delete(hallRef);
                    }
                }

                // 3. Upsert new/updated halls
                for (const [hallName, hallData] of Object.entries(hierarchy)) {
                    const hallRef = doc(db, COLLECTION_LOCATIONS, locationName, 'halls', hallName);
                    batch.set(hallRef, hallData);
                }

                await batch.commit();
            } else {
                // Just save the flat data (e.g. metadata updates)
                await setDoc(docRef, flattenedData, { merge: true });
            }
        } catch (error) {
            console.error("Error saving location:", error);
            throw error;
        }
    },

    async getLocation(locationName) {
        if (!locationName) return null;
        const docRef = doc(db, COLLECTION_LOCATIONS, locationName);

        let docSnap;
        try {
            // Standard fetch (Cache first, then Server if needed/configured)
            // We removed getDocFromServer to improve reliability on flaky connections/permissions
            docSnap = await getDoc(docRef);
        } catch (e) {
            console.error(`[locationService] Fetch failed for ${locationName}`, e);
            return null;
        }

        if (docSnap.exists()) {
            const data = docSnap.data();

            // 1. Try fetching from 'halls' subcollection (Primary Source)
            let hasSubcollectionData = false;
            try {
                const hallsCol = collection(db, COLLECTION_LOCATIONS, locationName, 'halls');
                const hallsSnap = await getDocs(hallsCol);

                if (!hallsSnap.empty) {
                    data.hierarchy = {};
                    hallsSnap.forEach(hallDoc => {
                        data.hierarchy[hallDoc.id] = hallDoc.data();
                    });
                    hasSubcollectionData = true;
                }
            } catch (err) {
                console.warn(`[locationService] Failed to check subcollections for ${locationName}:`, err);
            }

            // 2. Fallback to Storage URL (Legacy) if no subcollection data found
            if (!hasSubcollectionData && data.hierarchyUrl && (!data.hierarchy || Object.keys(data.hierarchy).length === 0)) {
                try {
                    const response = await fetch(data.hierarchyUrl);
                    if (response.ok) {
                        data.hierarchy = await response.json();
                    } else {
                        console.error('[locationService] Failed to fetch hierarchy from URL:', response.statusText);
                    }
                } catch (urlErr) {
                    console.error('[locationService] Error fetching hierarchy URL:', urlErr);
                }
            }

            return data;
        }
        return null;
    },

    // scope: null = alles (superuser), [] = niets, anders die locaties.
    // De collectiebrede query hieronder werkt onder de beveiligingsregels
    // alleen nog voor de superuser; iedere andere gebruiker krijgt zijn
    // locaties per document opgehaald.
    async getAllLocations(scope = null) {
        try {
            let docSnaps;
            if (scope === null) {
                const querySnapshot = await getDocs(collection(db, COLLECTION_LOCATIONS));
                docSnaps = querySnapshot.docs;
            } else if (scope.length === 0) {
                return {};
            } else {
                const snaps = await Promise.all(scope.map(loc =>
                    getDoc(doc(db, COLLECTION_LOCATIONS, loc))
                ));
                docSnaps = snaps.filter(s => s.exists());
            }

            const locations = {};
            const hydrationPromises = [];

            docSnaps.forEach((docSnap) => {
                const data = docSnap.data();
                locations[docSnap.id] = data;

                hydrationPromises.push(async () => {
                    try {
                        const hallsCol = collection(db, COLLECTION_LOCATIONS, docSnap.id, 'halls');
                        const hallsSnap = await getDocs(hallsCol);

                        if (!hallsSnap.empty) {
                            locations[docSnap.id].hierarchy = {};
                            hallsSnap.forEach(hallDoc => {
                                locations[docSnap.id].hierarchy[hallDoc.id] = hallDoc.data();
                            });
                        }
                        else if (data.hierarchyUrl && (!data.hierarchy || Object.keys(data.hierarchy).length === 0)) {
                            const response = await fetch(data.hierarchyUrl);
                            if (response.ok) {
                                locations[docSnap.id].hierarchy = await response.json();
                            } else {
                                console.error(`[LocationService] Failed to fetch hierarchy for ${docSnap.id}`);
                            }
                        }
                    } catch (error) {
                        console.error(`[LocationService] Error hydrating hierarchy for ${docSnap.id}:`, error);
                    }
                });
            });

            await Promise.all(hydrationPromises.map(p => p()));

            return locations;
        } catch (error) {
            console.error('[LocationService] getAllLocations failed:', error);
            throw error;
        }
    },

    async deleteLocation(name) {
        try {
            await deleteDoc(doc(db, COLLECTION_LOCATIONS, name));
        } catch (error) {
            console.error('[LocationService] deleteLocation failed:', error);
            throw error;
        }
    },

    // --- GRANULAR UPDATES ---
    async addInstanceToHall(locationName, hallName, lineName, sublineName, instanceData) {
        try {
            const hallRef = doc(db, COLLECTION_LOCATIONS, locationName, 'halls', hallName);

            // GUARD: Prevent system keys as names
            // Fix: Handle null/undefined sublineName safely
            const safeSubline = sublineName || '';

            if (['lines', 'sublines', 'halls', 'id', 'name'].includes(lineName.toLowerCase())) {
                throw new Error("Ongeldige naam: 'lines' etc. zijn gereserveerd voor Lijnen.");
            }
            if (safeSubline && ['lines', 'sublines', 'halls', 'id', 'name'].includes(safeSubline.toLowerCase())) {
                throw new Error("Ongeldige naam: 'lines' etc. zijn gereserveerd voor Sublijnen.");
            }

            const hallSnap = await getDoc(hallRef);

            if (!hallSnap.exists()) {
                throw new Error(`Hall ${hallName} not found in ${locationName}`);
            }

            const hallData = hallSnap.data();

            // 1. Detect Structure (Smart Mode)
            // CRITICAL FIX: If the target line ALREADY exists at the root, we MUST stay in Flat Mode.
            // This prevents a "Ghost 'lines' folder" from hijacking valid flat lines.
            const lineExistsAtRoot = hallData[lineName];
            const hasLinesContainer = hallData.lines && typeof hallData.lines === 'object';

            let targetLineContainer;

            if (lineExistsAtRoot) {
                // Line found at root -> FORCE FLAT MODE
                targetLineContainer = hallData;
            } else if (hasLinesContainer) {
                // Line not at root, but 'lines' container exists -> Use Nested Mode
                targetLineContainer = hallData.lines;
            } else {
                // Line not found anywhere, and no 'lines' container -> Default to Flat Mode (Legacy/Simple)
                targetLineContainer = hallData;
            }

            // 2. Ensure specific Line exists (STRICT MODE: No Auto-Creation)
            if (!targetLineContainer[lineName]) {
                // FAIL: Wizard cannot create Lines.
                throw new Error(`Line '${lineName}' not found in hierarchy (checked ${lineExistsAtRoot ? 'Root' : 'Nested'}). Wizard cannot create new structures.`);
            }

            // 3. Handle Subline (Smart Resolution: Flat vs Nested)
            let targetStorage;

            if (safeSubline) {
                // A. Check for Flat Subline (Direct child of Line) - PREFERRED for this User
                if (targetLineContainer[lineName][safeSubline]) {
                    targetStorage = targetLineContainer[lineName][safeSubline];
                }
                // B. Check for Nested Subline (Inside 'sublines' container)
                else if (targetLineContainer[lineName].sublines && targetLineContainer[lineName].sublines[safeSubline]) {
                    targetStorage = targetLineContainer[lineName].sublines[safeSubline];
                }
                // C. Not found anywhere -> STRICT ERROR
                else {
                    throw new Error(`Subline '${safeSubline}' NOT found on Line '${lineName}' (neither Direct nor in sublines). Wizard cannot create new structures.`);
                }
            } else {
                // Case: Direct Component (No Subline)
                targetStorage = targetLineContainer[lineName];
            }

            // 4. Init instances array if missing
            if (!targetStorage._instances) {
                targetStorage._instances = [];
            }

            // 5. Add Instance
            targetStorage._instances.push(instanceData);

            // 6. Save back to Firestore
            // If Nested, our modifications to 'targetLineContainer' (ref to hallData.lines) are already in hallData.
            // If Flat, 'targetLineContainer' IS hallData, so modifications are already in hallData.

            await setDoc(hallRef, hallData);
            return true;
        } catch (e) {
            console.error("Failed to add instance:", e);
            throw e;
        }
    },

    async removeInstanceFromHall(locationName, hallName, lineName, sublineName, photoUrl) {
        try {
            const hallRef = doc(db, COLLECTION_LOCATIONS, locationName, 'halls', hallName);
            const hallSnap = await getDoc(hallRef);

            if (!hallSnap.exists()) {
                throw new Error(`Hall ${hallName} not found in ${locationName}`);
            }

            const hallData = hallSnap.data();
            const subline = hallData.lines?.[lineName]?.sublines?.[sublineName];

            if (!subline) {
                // If subline doesn't exist, maybe it was already deleted or never existed?
                // We can consider this a "success" since the instance won't be there either.
                console.warn(`Subline path not found during removal: ${lineName} > ${sublineName}`);
                return true;
            }

            if (subline._instances && Array.isArray(subline._instances)) {
                const initialLength = subline._instances.length;
                // Filter out the instance with the matching URL
                // We normalize URLs to ensure robust matching
                const normalize = u => u ? u.split('?')[0] : '';
                const target = normalize(photoUrl);

                subline._instances = subline._instances.filter(i => normalize(i.photoUrl) !== target);

                if (subline._instances.length !== initialLength) {
                    await setDoc(hallRef, hallData);
                } else {
                    console.warn("Instance not found in hall hierarchy during removal.");
                }
            }
            return true;
        } catch (e) {
            console.error("Failed to remove instance:", e);
            throw e;
        }
    },

    async updateInstancePhoto(locationName, hallName, lineName, sublineName, oldUrl, newUrl) {
        try {
            const hallRef = doc(db, COLLECTION_LOCATIONS, locationName, 'halls', hallName);
            const hallSnap = await getDoc(hallRef);

            if (!hallSnap.exists()) {
                throw new Error(`Hall ${hallName} not found in ${locationName}`);
            }

            const hallData = hallSnap.data();
            const subline = hallData.lines?.[lineName]?.sublines?.[sublineName];

            if (!subline || !subline._instances) {
                console.warn("No instances found to update.");
                return false;
            }

            const normalize = u => u ? u.split('?')[0] : '';
            const target = normalize(oldUrl);

            let updated = false;
            subline._instances = subline._instances.map(inst => {
                if (normalize(inst.photoUrl) === target) {
                    updated = true;
                    return { ...inst, photoUrl: newUrl };
                }
                return inst;
            });

            if (updated) {
                await setDoc(hallRef, hallData);
                return true;
            }
            return false;
        } catch (e) {
            console.error("Failed to update instance photo:", e);
            throw e;
        }
    },

    async updateInstanceData(locationName, hallName, lineName, sublineName, instanceName, updates) {
        try {
            const hallRef = doc(db, COLLECTION_LOCATIONS, locationName, 'halls', hallName);
            const hallSnap = await getDoc(hallRef);

            if (!hallSnap.exists()) {
                throw new Error(`Hall ${hallName} not found in ${locationName}`);
            }

            const hallData = hallSnap.data();

            // Resolve target storage (Flat vs Nested - similar to addInstanceToHall)
            const safeSubline = sublineName || '';
            const lineExistsAtRoot = hallData[lineName];
            const hasLinesContainer = hallData.lines && typeof hallData.lines === 'object';

            let targetLineContainer = lineExistsAtRoot ? hallData : (hasLinesContainer ? hallData.lines : hallData);

            if (!targetLineContainer[lineName]) {
                throw new Error(`Line '${lineName}' not found in hierarchy.`);
            }

            let targetStorage;
            if (safeSubline) {
                if (targetLineContainer[lineName][safeSubline]) {
                    targetStorage = targetLineContainer[lineName][safeSubline];
                } else if (targetLineContainer[lineName].sublines && targetLineContainer[lineName].sublines[safeSubline]) {
                    targetStorage = targetLineContainer[lineName].sublines[safeSubline];
                } else {
                    throw new Error(`Subline '${safeSubline}' NOT found on Line '${lineName}'.`);
                }
            } else {
                targetStorage = targetLineContainer[lineName];
            }

            if (!targetStorage._instances || !Array.isArray(targetStorage._instances)) {
                console.warn("No instances found to update.");
                return false;
            }

            let updated = false;
            targetStorage._instances = targetStorage._instances.map(inst => {
                if (inst.name === instanceName) {
                    updated = true;
                    return { ...inst, ...updates };
                }
                return inst;
            });

            if (updated) {
                await setDoc(hallRef, hallData);
                return true;
            }
            return false;
        } catch (e) {
            console.error("Failed to update instance data:", e);
            throw e;
        }
    }
};
